import SEO from "../../../components/seo.jsx"
import { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);
const cinzel = { fontFamily: "'Cinzel', serif" };

const TESTIMONIALS = [
  {
    name: "Sagar Talwekar",
    quote:
      "The consultation was most satisfactory. Dr. Ajinkya Patil considered all aspects before and during surgery. The surgery was very smooth and I never felt any hesitation or anxiousness. My hairline has been brought back better than my expectation.",
  },
  {
    name: "Commander Rauf Shaikh",
    quote:
      "Was very sceptical to proceed with hair transplant due to doubts over effectiveness and budget. Dr. Ajinkya Patil was magical in his words and his approach made me ready to go ahead. He has smooth hands with injection, anaesthesia and surgery — an awesome setup and solid support staff.",
  },
  {
    name: "Sharvani Kadam",
    quote:
      "I visited for my hair fall and my husband got his hair transplant done here. We have never been so satisfied with a treatment. Dr. Ajinkya Patil was very patient in explaining the plan and carried out the surgery himself. Post-operative guidance was remarkable.",
  },
  {
    name: "Vivek Shukla",
    quote:
      "Would definitely recommend anyone planning a hair transplant, especially at an early age. The whole procedure is not as painful or hectic as it sounds — just care properly for 7 days after surgery. Worth the look you get back on your face and head.",
  },
  {
    name: "Bhupender Rasik",
    quote:
      "Great experienced doctor and team. I was worried before the transplant, but I enjoyed the complete process with correct information provided on a time-to-time basis. Highly recommended for transplant — this is the right place with the right solution.",
  },
];

/* Split into two staggered rows for the marquee, duplicated for a seamless loop */
const ROW_A = [...TESTIMONIALS.slice(0, 3), ...TESTIMONIALS.slice(0, 3)];
const ROW_B = [...TESTIMONIALS.slice(2), ...TESTIMONIALS.slice(2)];

function initials(name) {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function QuoteMark() {
  return (
    <svg width="26" height="20" viewBox="0 0 26 20" fill="none" className="text-[#c9a84c]/25">
      <path
        d="M0 20V11.7C0 4.9 4.4 0.6 10.8 0L11.6 3.2C7.9 4 5.9 6.2 5.6 9.5H10.4V20H0ZM14.4 20V11.7C14.4 4.9 18.8 0.6 25.2 0L26 3.2C22.3 4 20.3 6.2 20 9.5H24.8V20H14.4Z"
        fill="currentColor"
      />
    </svg>
  );
}

function TestimonialCard({ t }) {
  return (
    <article
      className="flex-shrink-0 w-[300px] sm:w-[340px] bg-white border border-[#c9a84c]/18 rounded-2xl p-6 sm:p-7 relative overflow-hidden mx-3"
    >
      <span className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent" />
      <QuoteMark />
      <p
        className="text-[13px] leading-[1.85] text-[#6b5535] mt-4 mb-6"
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 6,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {t.quote}
      </p>
      <div className="flex items-center gap-3">
        <div
          style={cinzel}
          className="w-11 h-11 rounded-full flex-shrink-0 flex items-center justify-center font-black text-[12px] text-[#1a0f02] bg-gradient-to-br from-[#c9943a] via-[#ffe9a0] to-[#c9943a]"
        >
          {initials(t.name)}
        </div>
        <div>
          <p style={cinzel} className="text-[12px] font-bold tracking-[0.04em] uppercase text-[#1a0f02]">
            {t.name}
          </p>
          <p className="text-[10.5px] tracking-[0.08em] uppercase text-[#a07828]/80">Verified Patient</p>
        </div>
      </div>
    </article>
  );
}

function MarqueeRow({ items, direction = "left", speed = 42 }) {
  return (
    <div className="marquee-row-mask">
      <div
        className="marquee-track"
        style={{
          animationDuration: `${speed}s`,
          animationDirection: direction === "left" ? "normal" : "reverse",
        }}
      >
        {items.map((t, i) => (
          <TestimonialCard key={`${t.name}-${i}`} t={t} />
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   PAGE COMPONENT
============================================================ */
export default function ClientTestimonials({ url = "https://pune.hairclinique.in/client-testimonials.php" }) {
  const pageRef = useRef(null);
  const headRef = useRef(null);
  const subRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const ease = "power3.out";
      gsap.fromTo(headRef.current, { opacity: 0, y: 22 }, { opacity: 1, y: 0, duration: 0.75, ease });
      gsap.fromTo(subRef.current, { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: 0.7, ease, delay: 0.12 });
    }, pageRef);
    return () => ctx.revert();
  }, []);

  return (
    <>
<SEO
  title="Client Testimonials | Hair Transplant Reviews | Dr. Ajinkya Patil"
  description="Real patient testimonials and hair transplant results from Clinique Internationale. See what our clients say about their Sapphire FUE journey with Dr. Ajinkya Patil."
  url={url}
/>
      <link
        href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=Raleway:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&display=swap"
        rel="stylesheet"
      />

      <style>{`
        .marquee-row-mask {
          position: relative;
          overflow: hidden;
          -webkit-mask-image: linear-gradient(90deg, transparent 0%, #000 8%, #000 92%, transparent 100%);
          mask-image: linear-gradient(90deg, transparent 0%, #000 8%, #000 92%, transparent 100%);
        }
        .marquee-track {
          display: flex;
          width: max-content;
          animation-name: marquee-scroll;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
        }
        .marquee-row-mask:hover .marquee-track {
          animation-play-state: paused;
        }
        @keyframes marquee-scroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .marquee-track { animation: none; }
        }
      `}</style>

      <div
        ref={pageRef}
        className="relative w-full bg-[#f2ece0] overflow-hidden py-16 sm:py-20 md:py-24 mt-20"
        style={{ fontFamily: "'Raleway', sans-serif" }}
      >
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 mb-12 sm:mb-14 text-center">
          <p
            style={cinzel}
            className="text-[10.5px] tracking-[0.28em] uppercase mb-3 flex items-center justify-center gap-2.5 text-[#a07828]"
          >
            <span className="w-6 h-px bg-[#c9a84c]" />
            Patient Stories
            <span className="w-6 h-px bg-[#c9a84c]" />
          </p>
          <h2
            ref={headRef}
            style={cinzel}
            className="font-black text-[1.5rem] sm:text-[1.9rem] md:text-[2.2rem] uppercase tracking-[0.04em] text-[#1a0f02] mb-4"
          >
            What Our Clients Say
          </h2>
          <p ref={subRef} className="text-[14px] sm:text-[15px] text-[#6b5535] max-w-xl mx-auto leading-relaxed">
            Real words from real patients who trusted us with their hair restoration journey.
          </p>
        </div>

        <div className="flex flex-col gap-6">
          <MarqueeRow items={ROW_A} direction="left" speed={46} />
          <MarqueeRow items={ROW_B} direction="right" speed={52} />
        </div>
      </div>
    </>
  );
}